import { FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Grid, Typography } from '@mui/material';
import { QuoteGetResponse } from '@lio/api-types';
import { HeaderContainer } from '@components/header/HeaderContainer';
import { HeaderType } from '@components/header/Header.constants';
import { FallbackHeader } from '@components/errorBoundary/FallbackHeader';
import { HeaderLogo } from '@components/header/HeaderLogo';
import { QuoteDescriptionHeader } from '@components/header/QuoteDescriptionHeader';
import { UserCircle } from '@assets/svgs';
import { ProfileBadge } from '@components/logout/ProfileBadge';
import { NotificationBadge } from '@components/notification/NotificationBadge';
import { useLayoutStore } from '@store/LayoutStore';
import { MobileHeaderMenu } from '@components/header/mobileHeader/MobileHeaderMenu';
import { HeaderButtonProps, HeaderButtons } from '@components/header/HeaderButton';
import { navLink } from '@components/header/Header';
import { shallow } from 'zustand/shallow';

interface IHeader {
  type: HeaderType;
  quote?: QuoteGetResponse;
  initialBgColor: any;
  showLogoutBtn?: boolean;
  showNavLinks?: boolean;
  navLinks?: navLink[];
  hasUnsavedChanges?: boolean;
  title?: string;
  buttons?: (HeaderButtonProps | React.ReactNode)[] | undefined;
  subLinkMenu?: React.ReactNode | undefined;
}

export const MobileHeader: FC<IHeader> = ({
  type = HeaderType.Nav,
  quote,
  showNavLinks = true,
  initialBgColor,
  showLogoutBtn = true,
  hasUnsavedChanges,
  navLinks,
  title,
  buttons,
  subLinkMenu,
}) => {
  const { showHeaderMenu } = useLayoutStore(
    (state) => ({
      showHeaderMenu: state.showHeaderMenu,
    }),
    shallow,
  );
  const headerHeight = {
    [HeaderType.Admin]: '60px',
    [HeaderType.NavTransparent]: '60px',
    [HeaderType.Nav]: '60px',
    [HeaderType.Detail]: '170px',
    [HeaderType.Payments]: '60px',
    [HeaderType.Settings]: '60px',
    [HeaderType.MakeAPayment]: '60px',
    [HeaderType.NewQuote]: title ? '100px' : '60px',
    [HeaderType.PlaceHolder]: '170px',
  };

  const headerStyle = {
    position: 'static',
    minHeight: headerHeight[type],
    height: 'auto',
    borderRadius: 0,
    background: initialBgColor,
    boxShadow: 'boxShadow.1',
    justifyContent: 'space-between',
  };

  return (
    <ErrorBoundary FallbackComponent={FallbackHeader}>
      <HeaderContainer headerStyle={headerStyle}>
        <Grid
          container
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            height: '100%',
            width: '100%',
          }}
        >
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              height: '60px',
            }}
          >
            {(showNavLinks || subLinkMenu) && (
              <MobileHeaderMenu
                forceShowHeaderButton={!!subLinkMenu}
                showNavLinks={showNavLinks}
                navLinks={navLinks}
                buttons={buttons}
              />
            )}
            <Grid
              sx={{
                marginTop: '0',
                '.MuiGrid-item': {
                  marginTop: type === HeaderType.Detail ? '0px' : '',
                },
              }}
            >
              <HeaderLogo
                iconStyle={{
                  height: '46px',
                  width: '46px',
                }}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            </Grid>
            <Grid>
              <Grid
                container
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <NotificationBadge />
                {showLogoutBtn && (
                  <ProfileBadge
                    iconButtonProps={{
                      sx: { color: 'common.white' },
                      children: <UserCircle width='20px' height='20px' />,
                    }}
                  />
                )}
                {/* If hamburger menu does not exist show buttons in header */}
                {!showNavLinks && <HeaderButtons buttons={buttons} />}
              </Grid>
            </Grid>
          </Grid>
          {title ? (
            <Grid
              container
              sx={{
                display: 'flex',
                alignItems: 'center',
                margin: 'auto',
                justifyContent: 'center',
              }}
            >
              <Typography variant='h5'>{title}</Typography>
            </Grid>
          ) : null}
          {quote && (
            <Grid
              container
              sx={{
                mt: showHeaderMenu ? 0 : 2,
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <QuoteDescriptionHeader {...quote} />
            </Grid>
          )}
          {showHeaderMenu && subLinkMenu && (
            <Grid
              container
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {subLinkMenu}
            </Grid>
          )}
        </Grid>
      </HeaderContainer>
    </ErrorBoundary>
  );
};
